import { useQuery } from '@tanstack/react-query';
import { me } from '@features/auth/api';

/**
 * Query keys for the auth namespace.
 *
 * Layered `namespace → kind` so a whole-namespace invalidate is possible —
 * see docs/frontend/server-state-migration.md, discipline 1.
 */
export const authKeys = {
  all: ['auth'],
  session: () => ['auth', 'session'],
};

/** Name of the channel tabs use to tell each other the session changed. */
export const AUTH_CHANNEL = 'auth';

/**
 * Tell every OTHER tab in this browser that the session changed.
 *
 * A session is a cookie, and a cookie belongs to the browser, not to a tab.
 * Any tab can sign in, sign out, or sign in AS SOMEONE ELSE, and the rest have
 * no way to notice — cookies fire no events. Without this, the other tabs keep
 * rendering whoever they last saw while their requests execute as whoever the
 * cookie now names. Nothing errors; the screen simply stops matching the
 * server.
 *
 * BroadcastChannel never delivers to the context that posted, so the caller
 * updates its own cache directly and this handles everyone else.
 *
 * Guarded because the API is absent in some test environments and older
 * browsers. Losing cross-tab sync there degrades to today's behaviour rather
 * than throwing.
 */
export function announceAuthChange() {
  if (typeof BroadcastChannel === 'undefined') return;

  const channel = new BroadcastChannel(AUTH_CHANNEL);
  channel.postMessage({ type: 'auth-changed' });
  channel.close();
}

/**
 * Who the server says you are, as server state rather than a snapshot.
 *
 * This used to be a `useState` filled once by a mount effect, which is what
 * made every stale-tab bug possible: the value could only ever be as fresh as
 * the moment the tab loaded. As a query it can be invalidated by a sibling tab
 * and revalidated on focus.
 *
 * `/api/auth/status` answers 200 in both states with `{ authenticated, user }`,
 * so there is no 401 to handle — an unauthenticated session is `null`, a
 * normal value, not an error.
 */
export async function fetchAuthSession() {
  const { data } = await me();
  return data?.authenticated ? (data.user ?? null) : null;
}

/**
 * The one description of the session query, shared by the hook and by
 * resetAuthSession so the two can never drift on key or options.
 */
export const authSessionQuery = {
  queryKey: authKeys.session(),
  queryFn: fetchAuthSession,
  // Overrides the global staleTime: 60_000 and refetchOnWindowFocus: false.
  //
  // Both defaults are right for ordinary resources and wrong for this one.
  // Identity is the single thing that can change while the user is looking at
  // a DIFFERENT tab, which is exactly the window refetchOnWindowFocus exists
  // for; and serving a cached identity for up to a minute means acting as the
  // wrong person for up to a minute.
  staleTime: 0,
  refetchOnWindowFocus: true,
  // Overrides the global retry: 1. Every guard in the app waits on this query,
  // so retrying holds the whole first paint behind a backoff delay for what is
  // only ever a network failure — the endpoint answers 200 whether or not you
  // are signed in.
  //
  // Failing straight to "not authenticated" is self-healing now: the user
  // lands on /login and the moment a refetch succeeds GuestOnly sends them
  // back. Before this query existed, the same failure stranded them there.
  retry: false,
};

export function useAuthSession() {
  return useQuery(authSessionQuery);
}

/**
 * Throw away everything this tab cached and re-ask who it is talking to.
 *
 * Called when a sibling tab reports a session change.
 *
 * Note what this does NOT do: `queryClient.clear()`. Clearing removes every
 * query including the session itself, and a mounted observer holds a reference
 * to the Query object it was given — it does not rebind to the replacement.
 * The tab would drop all its data and then go on displaying the identity it
 * already had, which is the exact failure this whole mechanism exists to stop.
 *
 * So: remove every OTHER namespace, then invalidate auth, which refetches
 * through the live observer.
 *
 * signIn / signOut may still use `clear()` because they follow it with
 * `setQueryData`, which notifies the cache and rebinds the observer. Refetching
 * does not. The two paths differ for that reason, not by accident.
 */
export async function resetAuthSession(queryClient) {
  queryClient.removeQueries({
    predicate: (query) => query.queryKey?.[0] !== authKeys.all[0],
  });
  await queryClient.invalidateQueries({ queryKey: authKeys.all });
}
